import React, { useState, useEffect, useMemo } from 'react';
import { Smartphone, Tablet, Monitor, X, ExternalLink } from 'lucide-react';
import { ClientEntity } from '../types';
import { injectClientLoginButton } from '../utils/loginInjector';
import { extractBrandingFromHtml } from '../utils/brandExtractor';
import { hostPublicoDoSite, urlPublicaDoSite } from '../lib/site-publico';

interface SitePreviewProps {
  client: ClientEntity;
  onClose: () => void;
}

export const SitePreview: React.FC<SitePreviewProps> = ({ client, onClose }) => {
  const [device, setDevice] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [iframeSrc, setIframeSrc] = useState<string>('');

  // Generate Safe HTML with Link Injection for Preview
  const safeHtml = useMemo(() => {
    let html = client.htmlContent;
    const brand = extractBrandingFromHtml(html, client.data);
    
    // Calculate WhatsApp link inside this scope
    const rawPhone = client.data.phoneNumber.replace(/[^0-9]/g, '');
    const waLink = rawPhone ? `https://wa.me/55${rawPhone}?text=Ol%C3%A1!%20Vim%20pelo%20site.` : '#';

    // Injeta botão de login mágico
    html = injectClientLoginButton(html, client.slug, client.data?.name, brand.primaryColor);

    const fixScript = `
      <script>
        document.addEventListener('DOMContentLoaded', function() {
          var clientWa = "${waLink}";

          document.body.addEventListener('click', function(e) {
            var target = e.target.closest('a');
            if (!target) return;
            var href = target.getAttribute('href');
            
            // 1. Prevent "Dead" links (#) from resetting iframe/parent hash
            if (!href || href === '#' || href === 'javascript:void(0)' || href === '') {
                e.preventDefault();
                e.stopPropagation();
                // 2. Redirect to WhatsApp
                if (clientWa && clientWa !== '#') {
                    window.open(clientWa, '_blank');
                }
                return;
            }

            // Login route interception
            if (href && (href.includes('#/login') || href.includes('/login'))) {
               e.preventDefault();
               e.stopPropagation();
               var targetHash = href.startsWith('#') ? href : ('#' + href.replace(/^[^#]*#/, ''));
               try { if (window.top) { window.top.location.hash = targetHash; } } catch(err) {}
               try { if (window.parent) { window.parent.postMessage({ type: 'NAVIGATE', hash: targetHash }, '*'); window.parent.location.hash = targetHash; } } catch(err) {}
               window.location.hash = targetHash;
               return;
            }

            // Protocols that must break out of iframe
            if (
               href.startsWith('tel:') || 
               href.startsWith('mailto:') || 
               href.startsWith('http') || 
               href.startsWith('https') || 
               href.startsWith('//') || 
               href.includes('wa.me') || 
               href.includes('whatsapp') ||
               href.includes('instagram') ||
               href.includes('facebook')
            ) {
               e.preventDefault();
               e.stopPropagation();
               window.open(href, '_blank');
            }
          }, true);
        });
      </script>
    `;

    if (html.includes('</body>')) {
        return html.replace('</body>', fixScript + '</body>');
    }
    return html + fixScript;
  }, [client]);

  useEffect(() => {
    // Create a Blob URL for the HTML content to render it in the iframe
    const blob = new Blob([safeHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Use setTimeout to avoid synchronous setState warning
    const timeoutId = setTimeout(() => {
      setIframeSrc(url);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
    };
  }, [safeHtml]);

  // O endereco onde o site responde de verdade: `<slug>.<dominio base>`,
  // servido pelo Worker da borda a partir do R2. Era
  // `window.location.origin + '/#/site/' + slug`, que aponta para o painel e
  // exige sessao de operador — nao servia para mandar para o cliente.
  const publicLink = urlPublicaDoSite(client);
  const publicHost = hostPublicoDoSite(client);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#FAFAFA] font-sans">
      {/* Header Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white shadow-sm z-10">
        <div className="flex items-center gap-4">
          <h2 className="font-semibold text-sm text-gray-900 tracking-tight">{client.data.name}</h2>
          <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-gray-100 text-gray-600 border border-gray-200">
            Preview
          </span>
        </div>

        <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-lg border border-gray-200">
          <button
            onClick={() => setDevice('mobile')}
            className={`p-1.5 rounded-md transition-all ${device === 'mobile' ? 'bg-white text-black shadow-sm' : 'text-gray-400 hover:text-gray-900'}`}
            title="Mobile View"
          >
            <Smartphone size={16} />
          </button>
          <button
            onClick={() => setDevice('tablet')}
            className={`p-1.5 rounded-md transition-all ${device === 'tablet' ? 'bg-white text-black shadow-sm' : 'text-gray-400 hover:text-gray-900'}`}
            title="Tablet View"
          >
            <Tablet size={16} />
          </button>
          <button
            onClick={() => setDevice('desktop')}
            className={`p-1.5 rounded-md transition-all ${device === 'desktop' ? 'bg-white text-black shadow-sm' : 'text-gray-400 hover:text-gray-900'}`}
            title="Desktop View"
          >
            <Monitor size={16} />
          </button>
        </div>

        <div className="flex items-center gap-4">
          <a 
            href={publicLink}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-black transition-colors"
          >
            <ExternalLink size={14} />
            Abrir Link
          </a>
          <div className="w-px h-4 bg-gray-200"></div>
          <button 
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 rounded-md transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Iframe Container */}
      <div className="flex-1 overflow-hidden bg-[#FAFAFA] flex items-center justify-center p-4 md:p-8 relative">
        <div 
          className={`transition-all duration-500 bg-white shadow-xl overflow-hidden relative ${
            device === 'mobile' ? 'w-[375px] h-[667px] rounded-[40px] border-[12px] border-black shrink-0' :
            device === 'tablet' ? 'w-[768px] h-[1024px] rounded-[24px] border-[12px] border-black shrink-0' :
            'w-full h-full max-w-[1440px] rounded-xl border border-gray-200'
          }`}
        >
          {(device === 'mobile' || device === 'tablet') && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-20"></div>}
          <iframe
            src={iframeSrc || undefined}
            title="Client Site Preview"
            className="w-full h-full bg-white"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation"
          />
        </div>
        
        {/* Dynamic Domain Label Overlay (Visual Flair) */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full text-xs font-medium text-gray-600 border border-gray-200 shadow-sm pointer-events-none">
          {publicHost}
        </div>
      </div>
    </div>
  );
};